import { Dictionary, KeyValuePair } from "../Utils/Dictionary";
import { ObjectSerializer } from "../Serialization/ObjectSerializer";
import { ObjSerUtils } from "../Serialization/ObjSerUtils";
import { ValueEncoder } from "../Utils/ValueEncoder";


export class DeviceClass implements ObjectSerializer {

	public CollectorProfile: CollectorProfile;
	public CapabilityProfile: CapabilityProfile;

	constructor() {
		this.CollectorProfile = new CollectorProfile();
		this.CapabilityProfile = new CapabilityProfile();
	}

	public Equals(obj: any): boolean {
		if (obj == this)
			return true;
		if (obj == null)
			return false;
		if (!(obj instanceof DeviceClass))
			return false;

		var other = <DeviceClass>obj;

		return (this.CollectorProfile.Equals(other.CollectorProfile) &&
			this.CapabilityProfile.Equals(other.CapabilityProfile));
	}

	public Write(obj: any, context: any): void {
		obj.device_class = {};
		obj = obj.device_class;

		if (!this.CollectorProfile.IsEmpty)
			this.CollectorProfile.Write(obj, context);

		if (!this.CapabilityProfile.IsEmpty)
			this.CapabilityProfile.Write(obj, context);
	}

	public Read(obj: any, context: any): void {
		if (!obj.device_class)
			throw new Error();
		obj = obj.device_class;

		if (obj.collector_profile !== undefined)
			this.CollectorProfile.Read(obj, context);

		if (obj.capability_profile !== undefined)
			this.CapabilityProfile.Read(obj, context);
	}
}


export class CollectorProfile implements ObjectSerializer {

	public CollectorName: string;
	public SourceName: string;
	public SourceDetails: string;

	constructor() {
		this.Clear();
	}

	public get HasCollectorName(): boolean {
		return !!this.CollectorName;
	}

	public get HasSourceName(): boolean {
		return !!this.SourceName;
	}

	public get HasSourceDetails(): boolean {
		return !!this.SourceDetails;
	}

	public get IsEmpty(): boolean {
		return !this.HasCollectorName;
	}

	public Clear(): void {
		this.CollectorName = "";
		this.SourceName = "";
		this.SourceDetails = "";
	}

	public Equals(obj: any): boolean {
		if (obj == this)
			return true;
		if (obj == null)
			return false;
		if (!(obj instanceof CollectorProfile))
			return false;

		var other = <CollectorProfile>obj;

		return (this.CollectorName == other.CollectorName &&
			this.SourceName == other.SourceName &&
			this.SourceDetails == other.SourceDetails);
	}

	public Write(obj: any, context: any): void {
		obj.collector_profile = {};
		obj = obj.collector_profile;

		if (!this.IsEmpty) {
			let e: any = {};
			e.collector_name = this.CollectorName;
			if (this.HasSourceName)
				e.source_name = this.SourceName;
			if (this.HasSourceDetails)
				e.source_details = this.SourceDetails;

			obj.entries = [{ entry: e }];
		}
	}

	public Read(obj: any, context: any): void {
		if (!obj.collector_profile)
			throw new Error();
		obj = obj.collector_profile;

		this.Clear();

		if (obj.entries !== undefined) {
			let e = obj.entries[0].entry;
			if (e.collector_name !== undefined)
				this.CollectorName = e.collector_name;
			if (e.source_name !== undefined)
				this.SourceName = e.source_name;
			if (e.source_details !== undefined)
				this.SourceDetails = e.source_details;
		}
	}
}


export class CapabilityProfileEntry {

	public CapabilityName: string;
	public CapabilityProviderName: string;
	public SourceName: string;
	public SourceDetails: string;

	constructor(capabilityName: string = "", capabilityProviderName: string = "",
		sourceName: string = "", sourceDetails: string = "") {

		this.CapabilityName = capabilityName;
		this.CapabilityProviderName = capabilityProviderName;
		this.SourceName = sourceName;
		this.SourceDetails = sourceDetails;
	}

	public get HasCapabilityName(): boolean {
		return !!this.CapabilityName;
	}

	public get HasCapabilityProviderName(): boolean {
		return !!this.CapabilityProviderName;
	}

	public get HasSourceName(): boolean {
		return !!this.SourceName;
	}

	public get HasSourceDetails(): boolean {
		return !!this.SourceDetails;
	}

	public Equals(obj: any): boolean {
		if (obj == this)
			return true;
		if (obj == null)
			return false;
		if (!(obj instanceof CapabilityProfileEntry))
			return false;

		var other = <CapabilityProfileEntry>obj;

		return (this.CapabilityName == other.CapabilityName &&
			this.CapabilityProviderName == other.CapabilityProviderName &&
			this.SourceName == other.SourceName &&
			this.SourceDetails == other.SourceDetails);
	}
}


export class CapabilityProfile implements ObjectSerializer {

	private _Entries: Dictionary<string, CapabilityProfileEntry>;

	constructor() {
		this._Entries = new Dictionary<string, CapabilityProfileEntry>();
	}

	public get IsEmpty(): boolean {
		return (this._Entries.Count == 0);
	}

	public Clear(): void {
		this._Entries.Clear();
	}

	public Equals(obj: any): boolean {
		if (obj == this)
			return true;
		if (obj == null)
			return false;
		if (!(obj instanceof CapabilityProfile))
			return false;

		var other = <CapabilityProfile>obj;

		if (this._Entries.Count != other._Entries.Count)
			return false;

		let result = true;

		this._Entries.ForEach((pair) => {
			let key = pair.Key;
			let myEntry = pair.Value;
			if (other._Entries.ContainsKey(key)) {
				let hisEntry = other._Entries.Item(key);
				if (!myEntry.Equals(hisEntry))
					result = false;
			}
			else {
				result = false;
			}
		});

		return result;
	}

	public AddEntry(capabilityName: string, capabilityProviderName: string,
		sourceName: string, sourceDetails: string): void {

		if (this._Entries.ContainsKey(capabilityName))
			this._Entries.Remove(capabilityName);

		this._Entries.Add(capabilityName,
			new CapabilityProfileEntry(capabilityName, capabilityProviderName, sourceName, sourceDetails));
	}

	public FindEntry(capabilityName: string): CapabilityProfileEntry {
		if (this._Entries.ContainsKey(capabilityName))
			return this._Entries.Item(capabilityName);
		else
			return null;
	}

	public Write(obj: any, context: any): void {
		obj.capability_profile = {};
		obj = obj.capability_profile;

		if (!this.IsEmpty) {
			obj.entries = [];
			ObjSerUtils.WriteCollection(obj.entries, this._Entries.Values(),
				(value: CapabilityProfileEntry) => {
					let e: any = {};
					e.capability_name = value.CapabilityName;
					e.capability_provider_name = value.CapabilityProviderName;
					if (value.HasSourceName)
						e.source_name = value.SourceName;
					if (value.HasSourceDetails)
						e.source_details = value.SourceDetails;
					return { entry: e };
				}, context);
		}
	}

	public Read(obj: any, context: any): void {
		if (!obj.capability_profile)
			throw new Error();
		obj = obj.capability_profile;

		this.Clear();

		if (obj.entries !== undefined) {
			obj.entries.forEach((value: any) => {
				let e = value.entry;
				this.AddEntry(e.capability_name, e.capability_provider_name,
					e.source_name, e.source_details);
			}, this);
		}
	}
}
